const toText = (value: unknown) => String(value ?? "").trim();

type MarketDomainOptions = {
  fresh?: boolean;
  explicitMarketDomain?: string;
  language?: string;
};

const readCurrentBrowserHost = (): string => {
  if (typeof window === "undefined") return "";
  return toText(window.location.hostname);
};

const readStoredLanguage = (): string => {
  if (typeof window === "undefined") return "";
  try {
    return toText(localStorage.getItem("volqan.consumer.language"));
  } catch {
    return "";
  }
};

const readStoredTerminalConfigSellingPointId = (): string => {
  if (typeof window === "undefined") return "";
  try {
    const raw = localStorage.getItem("terminalConfig");
    if (!raw) return "";
    const parsed = JSON.parse(raw) as { sellingPointId?: unknown };
    return toText(parsed?.sellingPointId);
  } catch {
    return "";
  }
};

const replaceTemplateId = (template: string, sellingPointId: string): string =>
  template
    .replace(/\{restaurantId\}/g, sellingPointId)
    .replace(/\{sellingPointId\}/g, sellingPointId);

const isLegacyLocalConsumerApiBase = (value: string): boolean => {
  const text = toText(value);
  if (!text) return false;
  try {
    const url = new URL(text);
    const host = toText(url.hostname);
    const port = toText(url.port);
    return (host === "127.0.0.1" || host === "localhost") && (port === "3014" || port === "3000");
  } catch {
    return (
      text.includes("127.0.0.1:3014") ||
      text.includes("localhost:3014") ||
      text.includes("127.0.0.1:3000") ||
      text.includes("localhost:3000")
    );
  }
};

export const resolveConsumerRuntimeMarketDomain = (options?: MarketDomainOptions): string => {
  const explicitMarketDomain = toText(options?.explicitMarketDomain);
  if (explicitMarketDomain) return explicitMarketDomain;

  const freshHost = readCurrentBrowserHost();
  if (options?.fresh && freshHost) return freshHost;

  return (
    toText(localStorage.getItem("consumerRuntimeMarketDomain")) ||
    toText((import.meta.env as Record<string, string | undefined>).VITE_MARKET_DOMAIN) ||
    freshHost
  );
};

export const resolveConsumerRuntimeMarketWebBaseUrl = (): string => {
  const explicitBase = toText(
    (import.meta.env as Record<string, string | undefined>).VITE_MARKET_WEB_URL
  );
  if (explicitBase) return explicitBase;
  const marketDomain = resolveConsumerRuntimeMarketDomain();
  return marketDomain ? `https://${marketDomain}` : "";
};

export const buildConsumerRuntimeOrderPayUrl = (orderId?: string | null): string => {
  const cleanOrderId = toText(orderId);
  const baseUrl = resolveConsumerRuntimeMarketWebBaseUrl();
  if (!cleanOrderId || !baseUrl) return "";
  try {
    return new URL(`/orders/${encodeURIComponent(cleanOrderId)}/pay`, baseUrl).toString();
  } catch {
    return "";
  }
};

export const resolveSellingPointId = (): string =>
  readStoredTerminalConfigSellingPointId()
  || toText(localStorage.getItem("sellingPointId"))
  || toText(localStorage.getItem("terminalId"))
  || toText((import.meta.env as Record<string, string | undefined>).VITE_SELLING_POINT_ID);

export const resolveConsumerRuntimeConnectionUuid = (): string => {
  const stored = toText(localStorage.getItem("connectionUuid"));
  if (stored) return stored;

  // Production devices must always be activated explicitly from /setup.
  // Build-time identifiers are only a local-development convenience: baking
  // one into a production bundle would configure every browser as one device.
  if (import.meta.env.PROD) return "";

  const envConnectionUuid =
    toText((import.meta.env as Record<string, string | undefined>).VITE_CONSUMER_RUNTIME_CONNECTION_UUID) ||
    toText((import.meta.env as Record<string, string | undefined>).VITE_QOMMERCE_CONNECTION_UUID);
  if (envConnectionUuid && typeof window !== "undefined") {
    try {
      localStorage.setItem("connectionUuid", envConnectionUuid);
    } catch {
      // Ignore storage failures; callers can still use the env fallback.
    }
  }
  return envConnectionUuid;
};

export const resolveConsumerRuntimeApiBase = (sellingPointId?: string): string => {
  const canonicalBase = import.meta.env.PROD
    ? "https://api.qommerce.io/touch-app/v1"
    : "https://dev-api.qommerce.io/touch-app/v1";
  const assertCanonical = (candidate: string): string => {
    const normalized = candidate.replace(/\/+$/, "");
    if (!import.meta.env.PROD) {
      try {
        const hostname = new URL(normalized).hostname;
        if (hostname === "127.0.0.1" || hostname === "localhost") return normalized;
      } catch {
        // Let the canonical validation below reject malformed URLs.
      }
    }
    if (!normalized.startsWith(canonicalBase)) {
      throw new Error(`Consumer runtime API must use ${canonicalBase}`);
    }
    return normalized;
  };
  const scopedSellingPointId = toText(sellingPointId) || resolveSellingPointId();
  const template = toText((import.meta.env as Record<string, string | undefined>).VITE_API_URL_TEMPLATE);
  if (template && scopedSellingPointId) return assertCanonical(replaceTemplateId(template, scopedSellingPointId));
  const envBase = toText((import.meta.env as Record<string, string | undefined>).VITE_API_URL);
  if (envBase) return assertCanonical(envBase);
  const storedBase = toText(localStorage.getItem("consumerRuntimeApiBaseUrl"));
  const browserHost = readCurrentBrowserHost();
  if (storedBase) {
    if (!(browserHost === "localhost" || browserHost === "127.0.0.1") || !isLegacyLocalConsumerApiBase(storedBase)) {
      return assertCanonical(storedBase);
    }
  }
  if (browserHost === "localhost" || browserHost === "127.0.0.1") {
    return canonicalBase;
  }
  return canonicalBase;
};

export const resolveConsumerRuntimeEventsBase = (_connectionUuid?: string): string => "";

export const resolveConsumerRuntimeMetadataUrl = (sellingPointId?: string): string => {
  const apiBase = resolveConsumerRuntimeApiBase(sellingPointId);
  if (!apiBase) return "";
  return `${apiBase.replace(/\/+$/, "")}/metadata`;
};

export const buildConsumerRuntimeHeaders = (
  connectionUuid?: string,
  options?: MarketDomainOptions
): Record<string, string> => {
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  const marketDomain = resolveConsumerRuntimeMarketDomain(options);
  const resolvedConnectionUuid = toText(connectionUuid) || resolveConsumerRuntimeConnectionUuid();
  const language = toText(options?.language) || readStoredLanguage() || "es";
  if (marketDomain) headers["x-market-domain"] = marketDomain;
  if (resolvedConnectionUuid) headers["x-connection-uuid"] = resolvedConnectionUuid;
  if (language) headers["x-lang"] = language;
  return headers;
};
